import { z } from "zod";
import {
  Currency,
  CustomerStatus,
  IsoDateTime,
  MembershipRole,
  RoleSource,
  StaffRole,
  UserStatus,
  Uuid,
} from "./common.ts";
import { Permission } from "./permissions.ts";

/*
 * Login over the customer's dataCycle instance and the session (07 §2.3, 08 §1, ADR-069,
 * ADR-078). The form starts with the e-mail address: the API looks the address up at the
 * central instance and either starts the OpenID flow with the instance it finds or asks the
 * portal for the instance address. Staff enter at the central instance without an instance
 * (ADR-065). Password accounts come in v1 (ADR-071); their routes are not here.
 */

/**
 * A normalized instance host: lower case, no scheme, path or port (07 §2.3). The schema
 * lowercases and trims; it rejects anything that is not a bare hostname, so a pasted URL fails
 * loudly instead of being guessed at.
 */
export const InstanceHost = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, "instanceHost must be a hostname");
export type InstanceHost = z.infer<typeof InstanceHost>;

/**
 * A relative path for the return after login; anything else falls back to `/` (07 §2.3). No
 * backslash anywhere: browsers read `\` in an http(s) URL as `/`, so `/\evil.example` would leave
 * the portal (H3, review 2026-10-19). No `//` anywhere either: dot segments resolve
 * `/.//evil.example` to the path `//evil.example`, which a browser reads as another host (RH1,
 * review of the follow-up 2026-10-19); a `//` in the query falls back to `/` with it. The
 * callback checks the origin once more and collapses leading slashes before it redirects.
 */
export const ReturnTo = z.string().regex(/^(?!.*\/\/)\/[^\s\\]*$/, "returnTo must be a relative path");

/** The e-mail address of the login form, trimmed and lower-cased before the lookup (ADR-078). */
export const LoginEmail = z.string().trim().toLowerCase().pipe(z.email());

/**
 * `POST /api/auth/login/start`. Exactly one of `email` (first step: the API looks the address
 * up at the central instance, ADR-078), `instanceHost` (second step after `next: "instance"`,
 * or a direct start over that instance) or `staff: true` (staff login at the central instance,
 * no instance parameter, ADR-065). The address never reaches the central instance as a
 * `login_hint` (ADR-069).
 */
export const LoginStartRequest = z
  .object({
    email: LoginEmail.optional(),
    instanceHost: InstanceHost.optional(),
    staff: z.literal(true).optional(),
    returnTo: ReturnTo.optional(),
  })
  .refine((v) => [v.email, v.instanceHost, v.staff].filter((x) => x !== undefined).length === 1, {
    message: "exactly one of email, instanceHost or staff",
    path: ["email"],
  });
export type LoginStartRequest = z.infer<typeof LoginStartRequest>;

/**
 * What the portal shows next when the lookup did not start the flow (07 §2.3). `instance`: the
 * address is unknown or has no instance, or has several; the portal asks for the instance host.
 * v1 adds `password` for accounts without an instance and `register` (ADR-071).
 */
export const LoginNext = z.enum(["instance"]);
export type LoginNext = z.infer<typeof LoginNext>;

/**
 * Either the flow started: the portal navigates to `authorizeUrl`, the login cookie `pp_login`
 * travels alongside. Or the API names the next step; `choices` lists the instances of an
 * address that belongs to several (F-104), the portal offers them instead of a free field.
 */
export const LoginStartResponse = z.union([
  z.object({ authorizeUrl: z.url() }),
  z.object({ next: LoginNext, choices: z.array(InstanceHost).min(2).optional() }),
]);
export type LoginStartResponse = z.infer<typeof LoginStartResponse>;

/**
 * Codes of `/login?error=<code>` after the callback (08 §1 Fehlerpfade). The callback never
 * renders an error page itself and never shows one of the central instance (ADR-060).
 */
export const LoginErrorCode = z.enum([
  /** forged or missing return: no cookie, no state; security log */
  "state",
  /** the login state is older than ten minutes; start again */
  "expired",
  /** the user aborted at the instance or was rejected there */
  "denied",
  /** instance or central instance not reachable */
  "upstream",
  /** the instance is not enabled at the central instance; points to the inquiry (UC-30) */
  "instance_not_enabled",
  /** `instanceDomains` of the signed-in user lacks the requested host; security log (ADR-069, ADR-078) */
  "instance_mismatch",
  /** the user already belongs to a customer of another instance (UC-22) */
  "other_instance",
]);
export type LoginErrorCode = z.infer<typeof LoginErrorCode>;

/**
 * `GET /api/auth/session` (07 §2.3). Without a session the API answers `401 unauthenticated`;
 * the portal turns that into "no session". `permissions` is the list of allowed actions from
 * the permission function; the portal shows and hides controls by it and never decides on its
 * own (05 §6.2). `customer` and `membership` are null for staff without a membership.
 */
export const SessionResponse = z.object({
  user: z.object({
    id: Uuid,
    email: z.email(),
    displayName: z.string().nullable(),
    status: UserStatus,
  }),
  customer: z
    .object({
      id: Uuid,
      name: z.string(),
      status: CustomerStatus,
      /** null once the team resolved the instance link (UC-22, 0010, AP-B8.5) */
      instanceHost: InstanceHost.nullable(),
      currency: Currency,
    })
    .nullable(),
  membership: z
    .object({
      role: MembershipRole,
      roleSource: RoleSource,
    })
    .nullable(),
  staffRole: StaffRole.nullable(),
  /** time of the login, for a fresh login before sensitive actions (06 `session.auth_time`) */
  authTime: IsoDateTime,
  /** absolute end of the session (REQ-16: thirty days, staff two hours) */
  expiresAt: IsoDateTime,
  permissions: z.array(Permission),
});
export type SessionResponse = z.infer<typeof SessionResponse>;
